import HexController from 'controllers/hex_controller';
import Sortable from 'sortablejs';
import { el } from 'modules/DOM';

export default class extends HexController {
  static targets = ['step', 'saveButton', 'cancelButton', 'formErrors'];

  static values = {
    newStepUrl: String,
    newConditionUrl: String,
    modifiable: Boolean
  };

  _dirty = false;
  _initialStepsGroupHTML = null;

  get dirty() {
    return this._dirty;
  }

  set dirty(value) {
    if (!this.modifiableValue) return;
    this._dirty = value;
    const canSave = this.stepTargets.length >= 1
                    && (this.stepTargets[0].querySelector('.manual-script-step-item.step-item-action textarea').value.length > 0
                        || this.stepTargets[0].querySelector('.manual-script-step-item.step-item-data textarea').value.length > 0
                        || this.stepTargets[0].querySelector('.manual-script-step-item.step-item-expected_result textarea').value.length > 0);
    this.saveButtonTarget.disabled = !this._dirty || !canSave;
    this.cancelButtonTarget.disabled = !this._dirty;
    if (this._dirty) {
      window.navigationLockController?.lock();
    } else {
      window.navigationLockController?.unlock();
    }
  }

  get testCaseController() {
    return this.element['ms--test-case'];
  }

  connect() {
    if (this.modifiableValue) {
      // If any of the textarea or input fields in the form (this.element) have been changed, set the dirty flag to true
      this.element.addEventListener('change', e => {
        if (e.target.tagName !== 'SELECT' || (e.target.name !== 'test-case-select' && e.target.name !== 'ms-strength-select')) {
          this.dirty = true
        }
      });

      // Initialize Sortable for drag-and-drop functionality
      this.initializeSortable();

      window.addEventListener('click', this.maybeStopEditing.bind(this));
      // Reset dirty flag on turbo form submit end
      this.element.addEventListener('turbo:submit-end', this.submitEnd.bind(this));
      this.element.addEventListener('turbo:submit-start', this.submitStart.bind(this));

      // Listen for CMD+S to save the form
      document.addEventListener('keydown', this.saveForm.bind(this));
      this.dirty = false;
    }
  }

  saveForm(e) {
    if (!this.modifiableValue) return;
    if (e.metaKey && e.key === 's') {
      e.preventDefault();
      this.saveButtonTarget.click();
    }
  }

  disconnect() {
    if (!this.modifiableValue) return;
    window.removeEventListener('click', this.maybeStopEditing.bind(this));
    window.navigationLockController?.unlock();
  }

  initializeSortable() {
    const container = this.element.querySelector('.manual-script-steps');
    Sortable.create(container, {
      handle: '.steps-group-handle',
      draggable: '.manual-script-step-group-container',
      animation: 150,
      onEnd: () => {
        this._updateStepsPosition();
        this.dirty = true;
      }
    });
  }

  addStep(e) {
    e.preventDefault();
    if (!this.modifiableValue) return;
    this.addStepAtPosition(this.stepTargets.length + 1, this.stepTargets[this.stepTargets.length - 1]);
  }

  addTopStep(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.modifiableValue) return;
    const firstStepGroupEl = this.element.querySelector('.manual-script-step-group-container');
    this.addStepAtPosition(0, firstStepGroupEl, 'beforebegin');
  }

  addMiddleStep(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.modifiableValue) return;
    const target = e.target.closest('a');
    const stepGroupEl = target.closest('.manual-script-step-group-container');
    const stepEls = stepGroupEl.querySelectorAll('.manual-script-step');
    const stepPosition = stepEls[stepEls.length - 1].dataset.stepPosition;
    this.addStepAtPosition(stepPosition, stepGroupEl);
  }

  addStepAtPosition(position, referenceGroupEl, adjacentPositioning = 'afterend') {
    const url = new URL(this.newStepUrlValue, window.location.origin);
    url.searchParams.set('step_position', position);

    // Load the new step group from the backend
    fetch(url.href)
      .then(response => response.text())
      .then(html => {
        const el = new DOMParser().parseFromString(html, 'text/html').body.querySelector('.manual-script-step-group-container');
        referenceGroupEl.insertAdjacentElement(adjacentPositioning, el);
        this._updateStepsPosition();
        this.dirty = true;
        this._startEditingGroup(el, el.querySelector('.manual-script-step-item.step-item-action textarea'));
      })
      .catch(e => console.error(e));
  }

  removeCondition(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.modifiableValue) return;
    const condition = e.target.closest('.manual-script-step-condition');
    const stepEl = condition.closest('.manual-script-step');
    const stepPosition = parseInt(stepEl.dataset.stepPosition);
    const conditionPosition = condition.dataset.conditionPosition;
    const conditionEls = stepEl.querySelectorAll('.manual-script-step-condition');
    condition.remove();
    conditionEls.forEach((conditionEl, condIndex) => {
      if (condIndex >= conditionPosition - 1) {
        this._updateStepConditionPosition(stepEl, stepPosition - 1, conditionEl, condIndex);
      }
    });
    this.dirty = true;
  }

  _updateStepsPosition() {
    const stepsGroups = this.element.querySelectorAll('.manual-script-step-group-container');
    stepsGroups.forEach((stepsGroup, index) => {
      // Left side
      const leftBar = stepsGroup.querySelector('.manual-script-step-group-left-bar');
      // Update the number
      const stepNumber = leftBar.querySelector('.group-index');
      stepNumber.textContent = index + 1;
      // Hide the move step up if it's the first step group
      leftBar.querySelector('.move-step-up').classList.toggle('hidden', index === 0);
      // Hide the move step down if it's the last step group
      leftBar.querySelector('.move-step-down').classList.toggle('hidden', index === stepsGroups.length - 1);
      // Right side
      const rightBar = stepsGroup.querySelector('.manual-script-step-group-right-bar');
      // Hide the remove step if there is only one step group
      rightBar.querySelector('.remove-step').classList.toggle('hidden', stepsGroups.length <= 1);
    });
    this.stepTargets.forEach((step, index) => {
      this._updateStepPosition(step, index);
    });
  }

  _updateStepPosition(step, index) {
    if (parseInt(step.dataset.stepPosition) === index + 1) {
      return;
    }
    const stepPositionEl = step.closest('.manual-script-step').querySelector(`input.step-position`);
    stepPositionEl.name = `step[${index}][position]`;
    stepPositionEl.id = `step_${index}_position`;
    stepPositionEl.value = index + 1;
    step.dataset.stepPosition = index + 1;
    this._updateStepItemsPosition(step, ['action', 'data', 'expected_result'], index);
    this._updateStepConditionsPosition(step, index);
  }

  _updateStepItemsPosition(step, items, index) {
    items.forEach(item => {
      const itemSelector = `.manual-script-step-item.step-item-${item}`;
      step.querySelector(`${itemSelector} label`).htmlFor = `step[${index}][${item}]`;
      const textarea = step.querySelector(`${itemSelector} textarea`);
      textarea.name = `step[${index}][${item}]`;
      textarea.id = `step_${index}_${item}`;
    });
  }

  _updateStepConditionsPosition(step, stepIndex) {
    const condSelector = '.manual-script-step-item.step-item-conditions';
    step.querySelector(`${condSelector} label`).htmlFor = `step[${stepIndex}][conditions]`;
    step.querySelectorAll(`${condSelector} .manual-script-step-condition`).forEach((condition, condIndex) => {
      this._updateStepConditionPosition(step, stepIndex, condition, condIndex);
    });
  }

  _updateStepConditionPosition(_step, stepIndex, condition, condIndex) {
    condition.dataset.conditionPosition = condIndex + 1;
    const parameterInput = condition.querySelector('.manual-script-step-condition-parameter');
    parameterInput.name = `step[${stepIndex}][conditions][${condIndex}][parameter]`;
    parameterInput.id = `step_${stepIndex}_conditions_${condIndex}_parameter`;
    const parameterNameInput = condition.querySelector('.manual-script-step-condition-parameter-name');
    parameterNameInput.name = `step[${stepIndex}][conditions][${condIndex}][parameter_name]`;
    parameterNameInput.id = `step_${stepIndex}_conditions_${condIndex}_parameter_name`;
    const valueInput = condition.querySelector('.manual-script-step-condition-value');
    valueInput.name = `step[${stepIndex}][conditions][${condIndex}][value]`;
    valueInput.id = `step_${stepIndex}_conditions_${condIndex}_value`;
    const condPositionEl = condition.querySelector(`input.condition-position`);
    condPositionEl.name = `step[${stepIndex}][conditions][${condIndex}][position]`;
    condPositionEl.id = `step_${stepIndex}_conditions_${condIndex}_position`;
    condPositionEl.value = condIndex + 1;
    const isToggleEl = condition.querySelector(`.manual-script-step-condition-is-toggle`);
    isToggleEl.name = `step[${stepIndex}][conditions][${condIndex}][is_toggle]`;
    isToggleEl.id = `step_${stepIndex}_conditions_${condIndex}_is_toggle`;
  }

  removeStep(e) {
    e.preventDefault();
    if (!this.modifiableValue) return;
    const target = e.target.closest('a');
    const stepGroupEl = target.closest('.manual-script-step-group-container');
    stepGroupEl.remove();
    this._updateStepsPosition();
    this.dirty = true;
  }

  addCondition(e) {
    e.preventDefault();
    if (!this.modifiableValue) return;
    const target = e.target.closest('a');
    const stepEl = target.closest('.manual-script-step');
    const positionEl = stepEl.querySelector('input.step-position');
    const stepPosition = positionEl.value;
    const conditionEls = stepEl.querySelectorAll('.manual-script-step-condition');
    const lastConditionEl = conditionEls[conditionEls.length - 1];
    const condPosition = lastConditionEl ? parseInt(lastConditionEl.dataset.conditionPosition) + 1 : 1;

    const url = new URL(this.newConditionUrlValue, window.location.origin);
    url.searchParams.set('step_position', stepPosition);
    url.searchParams.set('cond_position', condPosition);

    fetch(url.href)
      .then(response => response.text())
      .then(html => {
        const el = new DOMParser().parseFromString(html, 'text/html').body.querySelector('.manual-script-step-condition');
        stepEl.querySelector('.manual-script-step-conditions').append(el);
        this.dirty = true;
      })
      .catch(e => console.error(e));
  }

  moveStepUp(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.modifiableValue) return;
    const target = e.target.closest('a');
    const stepGroupEl = target.closest('.manual-script-step-group-container');
    const groupSteps = stepGroupEl.querySelectorAll('.manual-script-step');
    const firstStepEl = groupSteps[0];
    if (this.stepTargets.length <= 1 || parseInt(firstStepEl.dataset.stepPosition) <= 1) {
      return;
    }
    const previousStepGroupEl = stepGroupEl.previousElementSibling;
    // Swap the step groups
    stepGroupEl.remove();
    previousStepGroupEl.insertAdjacentElement('beforebegin', stepGroupEl);
    this._updateStepsPosition();
    this.dirty = true;
  }

  moveStepDown(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.modifiableValue) return;
    const target = e.target.closest('a');
    const stepGroupEl = target.closest('.manual-script-step-group-container');
    const groupSteps = stepGroupEl.querySelectorAll('.manual-script-step');
    const lastStepEl = groupSteps[groupSteps.length - 1];
    if (this.stepTargets.length <= 1 || parseInt(lastStepEl.dataset.stepPosition) >= this.stepTargets.length) {
      return;
    }
    const nextStepGroupEl = stepGroupEl.nextElementSibling;
    // Swap the step groups
    stepGroupEl.remove();
    nextStepGroupEl.insertAdjacentElement('afterend', stepGroupEl);
    this._updateStepsPosition();
    this.dirty = true;
  }

  getScript() {
    const script = [];
    this.stepTargets.forEach((stepEl, stepIndex) => {
      const step = {
        action: stepEl.querySelector(`#step_${stepIndex}_action`).value,
        data: stepEl.querySelector(`#step_${stepIndex}_data`).value,
        expected_result: stepEl.querySelector(`#step_${stepIndex}_expected_result`).value,
        position: parseInt(stepEl.querySelector(`#step_${stepIndex}_position`).value),
        conditions: []
      };

      const conditionElements = stepEl.querySelectorAll('.manual-script-step-condition');
      conditionElements.forEach((conditionEl, condIndex) => {
        const parameterInput = conditionEl.querySelector(`#step_${stepIndex}_conditions_${condIndex}_parameter`);
        if (parameterInput.value === '') {
          return;
        }
        const condition = {
          parameter_id: parameterInput.value,
          parameter: parameterInput.options[parameterInput.selectedIndex].text,
          value: conditionEl.querySelector(`#step_${stepIndex}_conditions_${condIndex}_value`).value,
          is_toggle: conditionEl.querySelector(`#step_${stepIndex}_conditions_${condIndex}_is_toggle`).value === 'true',
          position: parseInt(conditionEl.querySelector(`#step_${stepIndex}_conditions_${condIndex}_position`).value)
        };
        step.conditions.push(condition);
      });
      script.push(step);
    });
    return script;
  }

  _startEditingGroup(stepGroupEl, focusTarget = null) {
    // Start only if no other steps group is already editing
    if (stepGroupEl && !stepGroupEl.classList.contains('editing')) {
      this._initialStepsGroupHTML = stepGroupEl.innerHTML;
      stepGroupEl.classList.add('editing');
      // Show all the hidden conditions in the step group:
      stepGroupEl.querySelectorAll('.manual-script-step-item.step-item-conditions').forEach(condition => {
        condition.classList.remove('hidden');
        condition.querySelectorAll('.manual-script-step-condition').forEach(conditionEl => {
          conditionEl.classList.remove('hidden');
        });
      });
      // Focus the clicked element if possible
      if (focusTarget) {
        focusTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          focusTarget.focus();
          // Move cursor to the end of the textarea
          focusTarget.setSelectionRange(focusTarget.value.length, focusTarget.value.length);
        }, 100);
      }

      document.querySelector('#manual-script-panel').classList.add('ms-editing');
    }
  }

  startEditing(e) {
    if (!this.modifiableValue) return;
    // Call start editing with a slight delay to have time to stop editing the previous step if any
    setTimeout(() => {
      const target = e.target;
      const stepGroupEl = target.closest('.manual-script-step-group-container');
      if (!stepGroupEl) {
        return;
      }
      let focusTarget;
      if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
        focusTarget = target;
      } else {
        const manualScriptStepItemEl = target.closest('.manual-script-step-item.step-item-action, .manual-script-step-item.step-item-data, .manual-script-step-item.step-item-expected_result');
        if (manualScriptStepItemEl) {
          focusTarget = manualScriptStepItemEl.querySelector('textarea');
        } else {
          // Focus the first textarea or input in the step group
          const textarea = stepGroupEl.querySelector('.manual-script-step textarea.manual-script-step-action');
          focusTarget = textarea;
        }
      }
      this._startEditingGroup(stepGroupEl, focusTarget);
    }, 100);
  }

  maybeStopEditing(e) {
    const editingStepGroupEl = this.element.querySelector('.manual-script-step-group-container.editing');
    if (editingStepGroupEl && !editingStepGroupEl.contains(e.target)) {
      this.stopEditing(editingStepGroupEl);
    }
  }

  stopEditing(stepGroupEl) {
    stepGroupEl.classList.remove('editing');
    // Hide all the hidden conditions in the step group:
    stepGroupEl.querySelectorAll('.manual-script-step-item.step-item-conditions').forEach(condition => {
      const stepConditions = condition.querySelectorAll('.manual-script-step-condition');
      let hasParameterId = false;
      // Hide the single condition rows
      stepConditions.forEach(conditionEl => {
        if (conditionEl.querySelector('.manual-script-step-condition-parameter').value !== '') {
          hasParameterId = true;
        }
      });
      // Hide the whole conditions block if no condition has a parameter set
      if (!hasParameterId) {
        condition.classList.add('hidden');
      }
    });

    if (!this.dirty) {
      document.querySelector('#manual-script-panel').classList.remove('ms-editing');
    }
    this.testCaseController?.updateTestCasePreview();
    stepGroupEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  cancelEditing(e) {
    e.preventDefault();
    const stepGroupEl = e.target.closest('.manual-script-step-group-container');
    stepGroupEl.innerHTML = this._initialStepsGroupHTML;
    this._initialStepsGroupHTML = null;
    this.stopEditing(stepGroupEl);
  }

  doneEditing(e) {
    e.preventDefault();
    e.stopPropagation();
    const stepGroupEl = e.target.closest('.manual-script-step-group-container');
    this.stopEditing(stepGroupEl);
  }

  cloneStepGroup(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.modifiableValue) return;
    const stepsGroupEl = e.target.closest('.manual-script-step-group-container');
    const editingGroupEl = this.element.querySelector('.manual-script-step-group-container.editing');
    if (editingGroupEl) {
      this.stopEditing(editingGroupEl);
    }
    stepsGroupEl.insertAdjacentHTML('afterend', stepsGroupEl.outerHTML);
    this._updateStepsPosition();
    const addedStepsGroupEl = stepsGroupEl.nextElementSibling;
    addedStepsGroupEl.querySelector('.manual-script-step-item.step-item-action textarea').value = stepsGroupEl.querySelector('.manual-script-step-item.step-item-action textarea').value;
    addedStepsGroupEl.querySelector('.manual-script-step-item.step-item-data textarea').value = stepsGroupEl.querySelector('.manual-script-step-item.step-item-data textarea').value;
    addedStepsGroupEl.querySelector('.manual-script-step-item.step-item-expected_result textarea').value = stepsGroupEl.querySelector('.manual-script-step-item.step-item-expected_result textarea').value;
    this.dirty = true;
    this._startEditingGroup(addedStepsGroupEl, addedStepsGroupEl.querySelector('.manual-script-step-item.step-item-action textarea'));
  }

  cancelAllEditing(e) {
    e.preventDefault();
    window.navigationLockController?.unlock();
    window.location.reload();
  }

  toggleCondition(e) {
    e.preventDefault();
    if (!this.modifiableValue) return;
    this.dirty = true;
  }

  _currentScrollPosition = null;
  submitStart() {
    // Reset the form errors and avoid double submission
    this.formErrorsTarget.innerHTML = '';
    this.dirty = false;
    // Set the position of the current stepTarget visible in the page (scroll)
    const container = this.element.querySelector('.manual-script-steps');
    this._currentScrollPosition = container.scrollTop;
  }

  async submitEnd(event) {
    if (!event.detail.success) {
      event.preventDefault();
      let errorString = this.t('An error occurred while saving the manual script. Please try again.');
      if (event.detail.fetchResponse.contentType.toLowerCase().includes('application/json')) {
        try {
          const error = JSON.parse(await event.detail.fetchResponse.responseText);
          if (error?.error?.length > 0) {
            errorString = error.error;
          }
        } catch (e) {
          // Do nothing
          this.dbg(`Error parsing JSON of manual script error response:`, e);
        }
      } else if (event.detail?.message?.length > 0) {
        errorString = event.detail.message;
      }
      const errorMessage = el('div.aui-message.aui-message-error', errorString);
      this.formErrorsTarget.append(errorMessage);
      this.dirty = true;
    } else {
      this.dirty = false;
      setTimeout(() => {
        if (this._currentScrollPosition) {
          const container = document.querySelector('.manual-script-steps');
          container.scrollTop = this._currentScrollPosition;
          this._currentScrollPosition = null;
        }
      }, 10);
    }
  }
}
